/**
 * PaymentSuccess — v3.0.140
 * Landing page after a successful Stripe Checkout redirect.
 * Polls /stripe/checkout/status/:session_id until the DB confirms
 * `payment_status = 'paid'` (either the webhook or the poll-fallback
 * flipped it) and then shows the credits added.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Zap, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiFetch } from '../App';
import MainNav from '../components/MainNav';

const MAX_POLLS = 15; // ~30s total at 2s intervals

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | timeout | error
  const [txn, setTxn] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState('error');
      return;
    }
    try {
      const { data } = await apiFetch(`/stripe/checkout/status/${sessionId}`);
      setTxn(data);
      if (data?.payment_status === 'paid') {
        setState('paid');
        toast.success(`Added ${data.credits_purchased || ''} credits!`);
        return;
      }
      if (['failed', 'expired', 'refunded'].includes(data?.payment_status)) {
        setState('error');
        return;
      }
    } catch (err) {
      // Transient — keep polling until MAX_POLLS
      // eslint-disable-next-line no-console
      console.warn('Poll error:', err.message);
    }
    setAttempt((a) => a + 1);
  }, [sessionId]);

  useEffect(() => {
    if (state !== 'polling') return;
    if (attempt >= MAX_POLLS) {
      setState('timeout');
      return;
    }
    timerRef.current = setTimeout(poll, attempt === 0 ? 500 : 2000);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [attempt, poll, state]);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="text-center">
          <AlertTriangle className="mx-auto mb-3 text-amber-400" size={40} />
          <p className="text-zinc-400 mb-4">Missing session id.</p>
          <Link to="/dashboard" className="text-emerald-400 hover:underline">Go to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="payment-success-page">
      <MainNav />
      <div className="max-w-md mx-auto pt-24 px-4 text-center">
        {state === 'polling' && (
          <>
            <Loader2 className="mx-auto mb-4 animate-spin text-emerald-400" size={48} />
            <h1 className="text-2xl font-bold mb-2">Confirming your payment…</h1>
            <p className="text-sm text-zinc-400">
              This usually takes a couple of seconds.
            </p>
            <p className="text-xs text-zinc-600 mt-4">Attempt {attempt + 1} of {MAX_POLLS}</p>
          </>
        )}

        {state === 'paid' && (
          <>
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 className="text-emerald-400" size={40} />
            </div>
            <h1 className="text-3xl font-bold mb-2">Payment received 🎉</h1>
            <p className="text-sm text-zinc-400 mb-6">
              {txn?.credits_purchased} credits added to your account.
            </p>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 inline-flex items-center gap-2 mb-6">
              <Zap className="text-emerald-400" size={18} />
              <span className="text-lg font-bold text-emerald-300">
                {txn?.credits ?? '—'} credits total
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate('/create-song')}
                className="bg-emerald-500 text-black hover:bg-emerald-400"
                data-testid="payment-success-create-btn"
              >
                Make a Song Now
              </Button>
              <Link to="/dashboard" className="text-sm text-zinc-400 hover:text-white mt-2">
                Back to dashboard
              </Link>
            </div>
          </>
        )}

        {state === 'timeout' && (
          <>
            <AlertTriangle className="mx-auto mb-3 text-amber-400" size={40} />
            <h1 className="text-xl font-bold mb-2">Still confirming…</h1>
            <p className="text-sm text-zinc-400 mb-4">
              Stripe hasn't finished processing yet. Your card was likely charged;
              credits will land within a few minutes. Refresh this page or check your
              dashboard.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => { setState('polling'); setAttempt(0); }}
                className="bg-emerald-500 text-black hover:bg-emerald-400"
                data-testid="payment-success-retry-btn"
              >
                Check again
              </Button>
              <Link to="/dashboard" className="text-sm text-zinc-400 hover:text-white mt-2">
                Back to dashboard
              </Link>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <AlertTriangle className="mx-auto mb-3 text-rose-400" size={40} />
            <h1 className="text-xl font-bold mb-2">
              {txn?.payment_status === 'failed' ? 'Payment failed' : 'Something went wrong'}
            </h1>
            <p className="text-sm text-zinc-400 mb-4">
              {txn?.payment_status === 'failed'
                ? 'Your card was declined or the payment did not complete.'
                : 'We couldn\'t confirm the payment. Please contact support if you were charged.'}
            </p>
            <Link to="/dashboard" className="text-emerald-400 hover:underline">
              Back to dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
